import type { FlagKey, MeWorkspace, UserRole } from '@doneverse/shared'
import { matchesPathPrefix } from '@/lib/routes'

/**
 * Access policy for a restricted doer.
 *
 * Two kinds of doer are restricted to Home + Settings (+ Feedback and Doer
 * Profile, see below):
 * - one with no founder assigned, who can't do anything useful in a founder's
 *   workspace;
 * - one an admin has given the `doer-dashboard-hidden` override, whose
 *   dashboard isn't shipped to them yet (the DoneFlow agent rollout).
 *
 * This module is the single source of truth for that rule: the router enforces
 * it (blocked routes redirect to Home) and the menu reflects it (rail + sidebar
 * hide everything else). Both read `isRestrictedDoer` so they can never drift.
 */

/**
 * Workspaces a doer belongs to that someone ELSE founded. A doer can be a member
 * of a workspace they founded themselves (founder→doer switch); that
 * self-membership isn't a founder assignment. Soft-deleted workspaces don't
 * count. Mirrors the backend enrichment in admin-users.ts (`ws.founder_id !== user.id`).
 */
export function assignedFounderWorkspaces(userId: string, workspaces: MeWorkspace[]): MeWorkspace[] {
  return workspaces.filter((ws) => ws.founder_id !== userId && !ws.deleted_at)
}

type DoerAccessProfile = { id: string; role: UserRole; workspaces: MeWorkspace[] } | null

/**
 * True only for a `doer` with zero founder-assigned workspaces — the restricted
 * state. False for every other role and while the profile is still loading
 * (null), so guards and the menu never react before auth resolves.
 */
export function doerHasNoFounder(profile: DoerAccessProfile): boolean {
  if (!profile || profile.role !== 'doer') return false
  return assignedFounderWorkspaces(profile.id, profile.workspaces).length === 0
}

type HasFlag = (flag: FlagKey) => boolean

/**
 * True only for a `doer` with the `doer-dashboard-hidden` override. Other roles
 * ignore the flag, so a stray override can't lock a founder or admin out.
 */
export function doerDashboardHidden(profile: DoerAccessProfile, hasFlag: HasFlag): boolean {
  if (!profile || profile.role !== 'doer') return false
  return hasFlag('doer-dashboard-hidden')
}

/**
 * Whether the restricted-doer rule applies: no founder, or the dashboard is
 * hidden for them. Guards and menus call this; `doerHasNoFounder` and
 * `doerDashboardHidden` stay separate only so Home can say which one it is.
 */
export function isRestrictedDoer(profile: DoerAccessProfile, hasFlag: HasFlag): boolean {
  return doerHasNoFounder(profile) || doerDashboardHidden(profile, hasFlag)
}

/**
 * The only surfaces a restricted doer may reach. Everything else is hidden
 * from the menu and redirected to Home by the router.
 *
 * `/feedback` is included because the feedback button is on every page they can
 * reach — a doer who reports a bug has to be able to open the thread we reply in.
 *
 * `/doer-profile` is included because writing it is what a doer does BEFORE
 * they have a founder. It is the page their first founder reads to meet them,
 * so the doer with nobody assigned is exactly the one who should be filling it
 * in — bouncing them to Home would leave the profile blank until the moment it
 * was already needed.
 */
export const RESTRICTED_DOER_PATHS = ['/doer-home', '/settings', '/feedback', '/doer-profile'] as const

/** Where a blocked navigation lands — must itself be in RESTRICTED_DOER_PATHS. */
export const RESTRICTED_DOER_HOME = '/doer-home'

export function isAllowedForRestrictedDoer(pathname: string): boolean {
  return RESTRICTED_DOER_PATHS.some((p) => matchesPathPrefix(pathname, p))
}
